/**
 * 课文表单（家长端语文听写：新建 / 编辑一节课）。
 *
 * 课序 `lessonLabel` 是**原始写法**（"第5课" / "识字1" / "语文园地三"），
 * `lessonNo` 是排序用的数字。后端在 `lessonNo` 缺省时会从 `lessonLabel` 里取第一个数字，
 * 这里显式给一个输入框，并在留空时告诉我们"后端会自己取"。
 */
import { useEffect, useState } from 'react';
import { GRADES, describeTextbookLabel, gradeLabel } from '@fht/shared';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { semesterLabel } from '../../lib/api/dictation';
import type { LessonView, TextbookView } from '../../lib/api/dictation';

export interface LessonFormSubmit {
  textbookId: string;
  gradeCode: string;
  semester: string;
  title: string;
  lessonLabel: string;
  /** 留空 = 让后端从课序写法里取数字。 */
  lessonNo: number | null;
  unitNo: number | null;
}

export interface DictationLessonFormProps {
  open: boolean;
  editing: LessonView | null;
  textbooks: TextbookView[] | null;
  defaultTextbookId: string;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: LessonFormSubmit) => void;
}

const SEMESTERS = ['上', '下', '不分'];

export function DictationLessonForm({
  open,
  editing,
  textbooks,
  defaultTextbookId,
  submitting,
  error,
  onClose,
  onSubmit,
}: DictationLessonFormProps) {
  const [textbookId, setTextbookId] = useState(defaultTextbookId);
  const [gradeCode, setGradeCode] = useState('primary_3');
  const [semester, setSemester] = useState('上');
  const [lessonLabel, setLessonLabel] = useState('');
  const [title, setTitle] = useState('');
  const [lessonNo, setLessonNo] = useState('');
  const [unitNo, setUnitNo] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editing === null) {
      setTextbookId(defaultTextbookId);
      setGradeCode('primary_3');
      setSemester('上');
      setLessonLabel('');
      setTitle('');
      setLessonNo('');
      setUnitNo('');
      return;
    }
    setTextbookId(editing.textbookId);
    setGradeCode(editing.gradeCode);
    setSemester(editing.semester);
    setLessonLabel(editing.lessonLabel);
    setTitle(editing.title);
    setLessonNo(String(editing.lessonNo));
    setUnitNo(editing.unitNo === null ? '' : String(editing.unitNo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id, defaultTextbookId]);

  const canSubmit = title.trim() !== '' && textbookId !== '';

  return (
    <Modal
      open={open}
      title={editing === null ? '新建课文' : '编辑课文'}
      description="课文是「年级 + 学期 + 课序」下的一组字词。年级学期决定它出现在哪一册里。"
      onClose={onClose}
      footer={
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" disabled={submitting} onClick={onClose}>
            取消
          </Button>
          <Button
            loading={submitting}
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({
                textbookId,
                gradeCode,
                semester,
                title: title.trim(),
                lessonLabel: lessonLabel.trim(),
                lessonNo: lessonNo.trim() === '' ? null : Number(lessonNo),
                unitNo: unitNo.trim() === '' ? null : Number(unitNo),
              })
            }
          >
            保存
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Select label="课本版本" value={textbookId} onChange={(event) => setTextbookId(event.target.value)}>
          <option value="">请选择</option>
          {/*
            ⚠️ 带版次：课文挂在**某本书**上，而"哪本书"现在由「课本 + 版次」共同决定。
               只显示书名时，同一课本名下的两个版次会长得一样（选错=课文挂错书）。
          */}
          {(textbooks ?? []).map((textbook) => (
            <option key={textbook.id} value={textbook.id}>
              {describeTextbookLabel(textbook)}
            </option>
          ))}
        </Select>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select label="年级" value={gradeCode} onChange={(event) => setGradeCode(event.target.value)}>
            {GRADES.map((grade) => (
              <option key={grade.code} value={grade.code}>
                {gradeLabel(grade.code)}
              </option>
            ))}
          </Select>
          <Select label="学期" value={semester} onChange={(event) => setSemester(event.target.value)}>
            {SEMESTERS.map((value) => (
              <option key={value} value={value}>
                {semesterLabel(value)}
              </option>
            ))}
          </Select>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input
            label="课序写法"
            value={lessonLabel}
            placeholder="第5课 / 识字1 / 语文园地三"
            onChange={(event) => setLessonLabel(event.target.value)}
          />
          <Input
            label="课序数字（排序用）"
            value={lessonNo}
            inputMode="numeric"
            placeholder="留空则从课序写法里取"
            onChange={(event) => setLessonNo(event.target.value)}
          />
          <Input
            label="单元号（可选）"
            value={unitNo}
            inputMode="numeric"
            onChange={(event) => setUnitNo(event.target.value)}
          />
        </div>

        <Input
          label="课题"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="例如 秋天 / 小小的船"
        />

        {error !== null && (
          <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
